import React, { useState, useRef } from 'react';
import { Button, CircularProgress, Collapse, Divider, IconButton, Paper } from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import { isSpecialSectionType, SBSection, SourceBook, SPECIAL_SECTION_TYPES } from '@dndModels';
import StringBox from '@components/input/strings/StringBox';
import DescriptionBox from './DescriptionBox';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { DataType, FeatTier, ItemType } from '@dndModels/Types';

import API from '@utilities/smart_api';
import SelectStringBox from '@components/input/selects/SelectStringBox';
import CheckBox from '@components/input/CheckBox';
import { FEAT_TIERS, ITEM_TYPES } from '@dndModels/Constants';

const api = API.getInstance();

// ---------------------------------------------------------------------------
// Flat representation used internally for all mutations
// ---------------------------------------------------------------------------

interface FlatNode {
  section: SBSection;
  depth: number;
}

function flatten(sections: SBSection[], depth = 0): FlatNode[] {
  const result: FlatNode[] = [];
  for (const s of sections) {
    result.push({ section: s, depth });
    result.push(...flatten(s.subsections, depth + 1));
  }
  return result;
}

function unflatten(nodes: FlatNode[]): SBSection[] {
  function build(startIndex: number, parentDepth: number): { sections: SBSection[]; nextIndex: number } {
    const sections: SBSection[] = [];
    let i = startIndex;
    while (i < nodes.length && nodes[i].depth > parentDepth) {
      if (nodes[i].depth === parentDepth + 1) {
        const s = nodes[i].section.clone();
        i++;
        const { sections: children, nextIndex } = build(i, parentDepth + 1);
        s.subsections = children;
        sections.push(s);
        i = nextIndex;
      } else {
        i++;
      }
    }
    return { sections, nextIndex: i };
  }
  return build(0, -1).sections;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function computeParentInfo(
  nodes: FlatNode[],
  index: number
): { parent_id: string; parent_type: 'Source Book' | 'SBSection'; parentKey: string } {
  const depth = nodes[index].depth;
  if (depth === 0) {
    return {
      parent_id: nodes[index].section.parent_id ?? '',
      parent_type: 'Source Book',
      parentKey: `sb:${nodes[index].section.parent_id ?? ''}`,
    };
  }
  for (let i = index - 1; i >= 0; i--) {
    if (nodes[i].depth === depth - 1) {
      const pid = nodes[i].section.true_id;
      return { parent_id: pid, parent_type: 'SBSection', parentKey: `section:${pid}` };
    }
  }
  return { parent_id: '', parent_type: 'Source Book', parentKey: 'sb:' };
}

// ---------------------------------------------------------------------------
// SBSectionsEditor
// ---------------------------------------------------------------------------

interface SBSectionsEditorProps {
  sections: SBSection[];
  source_book: SourceBook;
}

export default function SBSectionsEditor({
  sections: initialSections,
  source_book,
}: SBSectionsEditorProps) {
  const [nodes, setNodes] = useState<FlatNode[]>(() => flatten(initialSections));
  // Always the latest nodes, so handlers that finish after an await never build on a stale copy.
  const nodesRef = useRef<FlatNode[]>(nodes);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editingIds, setEditingIds] = useState<Set<string>>(new Set());
  // true_ids currently being loaded via getFullObject
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  // true_id -> original SBSection snapshot for Cancel
  const [dirtyMap, setDirtyMap] = useState<Map<string, SBSection>>(new Map());
  // true_ids currently being saved
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  // true_ids with a most-recent save error
  const [errorIds, setErrorIds] = useState<Set<string>>(new Set());
  const [checkingMissing, setCheckingMissing] = useState(false);
  const [missingMessage, setMissingMessage] = useState('');

  const dragOverIndex = useRef<number | null>(null);
  // Structure saves run one at a time, and only the newest waiting structure gets sent,
  // so an older arrangement can never land after a newer one.
  const pendingStructure = useRef<FlatNode[] | null>(null);
  const savingStructure = useRef(false);

  /**
   * Every change to nodes goes through here so nodesRef stays current.
   */
  const commitNodes = (next: FlatNode[]) => {
    nodesRef.current = next;
    setNodes(next);
  };

  /**
   * Each section also records its own parent and position, which is what
   * Check for Missing Sections rebuilds from, so keep those in step with the structure.
   */
  const updatePlacements = (currentNodes: FlatNode[]): SBSection[] => {
    const parentIdAtDepth: string[] = [];
    const childCounts = new Map<string, number>();
    const changed: SBSection[] = [];
    for (const { section, depth } of currentNodes) {
      const parent_type: SBSection['parent_type'] = depth === 0 ? 'Source Book' : 'SBSection';
      const parent_id = depth === 0 ? source_book._id : parentIdAtDepth[depth - 1];
      const position = childCounts.get(parent_id) ?? 0;
      childCounts.set(parent_id, position + 1);
      parentIdAtDepth[depth] = section.true_id;
      if (section._id !== '' &&
        (section.parent_type !== parent_type || section.parent_id !== parent_id || section.position !== position)) {
        section.parent_type = parent_type;
        section.parent_id = parent_id;
        section.position = position;
        changed.push(section);
      }
    }
    return changed;
  };

  const savePlacements = async (changed: SBSection[]) => {
    // A book whose placements were never kept up can have a lot of these, so send them in batches.
    for (let i = 0; i < changed.length; i += 10) {
      await Promise.all(changed.slice(i, i + 10).map(s =>
        api.updateFieldsFullAndLight('sb_section', s, { parent_type: s.parent_type, parent_id: s.parent_id, position: s.position })
      ));
    }
  };

  /**
   * Save the source book's section structure using the provided nodes array.
   * Saves are queued, so if one is already running this one is sent when it finishes
   * (replacing any other that was waiting).
   */
  const saveStructure = async (currentNodes: FlatNode[]) => {
    pendingStructure.current = currentNodes;
    if (savingStructure.current) return;
    savingStructure.current = true;
    try {
      while (pendingStructure.current) {
        const toSave = pendingStructure.current;
        pendingStructure.current = null;
        const changed = updatePlacements(toSave);
        source_book.sections = unflatten(toSave);
        await api.updateObject("source_book", source_book, ["sections"]);
        await savePlacements(changed);
      }
    } finally {
      savingStructure.current = false;
    }
  };

  // ---------------------------------------------------------------------------
  // Expand / edit toggles
  // ---------------------------------------------------------------------------

  const toggleExpand = (id: string) =>
    setExpandedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const expand = (id: string) =>
    setExpandedIds(prev => new Set(prev).add(id));

  /**
   * Open the edit panel for a section. If the section hasn't been fully loaded
   * yet, fire getFullObject first and copy the result into the node before opening.
   */
  const openEditing = (tid: string, index: number) => {
    const section = nodesRef.current[index].section;

    // Already loaded — open immediately
    if (section.loaded) {
      setEditingIds(prev => new Set(prev).add(tid));
      return;
    }

    // Not yet loaded — fetch, then copy result into the node and open
    setLoadingIds(prev => new Set(prev).add(tid));
    api.getFullObject<SBSection>('sb_section', section._id).then(from_db => {
      if (from_db) {
        section.copy(from_db);
        commitNodes(nodesRef.current.map(n => n.section.true_id === tid ? { ...n, section } : n));
      }
      setLoadingIds(prev => { const n = new Set(prev); n.delete(tid); return n; });
      setEditingIds(prev => new Set(prev).add(tid));
    });
  };

  const closeEditing = (tid: string) =>
    setEditingIds(prev => { const n = new Set(prev); n.delete(tid); return n; });

  const toggleEditing = (tid: string, index: number) => {
    if (editingIds.has(tid)) {
      closeEditing(tid);
    } else {
      openEditing(tid, index);
    }
  };

  // ---------------------------------------------------------------------------
  // Dirty tracking
  // ---------------------------------------------------------------------------

  const markDirty = (id: string, original: SBSection) =>
    setDirtyMap(prev => {
      if (prev.has(id)) return prev;
      const next = new Map(prev);
      next.set(id, original.clone());
      return next;
    });

  const clearDirty = (id: string) =>
    setDirtyMap(prev => { const n = new Map(prev); n.delete(id); return n; });

  const isDirty = (id: string) => dirtyMap.has(id);

  // ---------------------------------------------------------------------------
  // API save
  // ---------------------------------------------------------------------------

  /**
   * Full upsert — used when creating new sections or saving name/content.
   * Handles swapping the placeholder true_id for the real _id on creation.
   * Returns the final nodes array after any id-swap so callers can use it
   * immediately (e.g. to call saveStructure with the correct ids).
   */
  const saveSectionContent = async (section: SBSection): Promise<{ saved: SBSection | null; finalNodes: FlatNode[] }> => {
    const tid = section.true_id;
    // createObject fills in section._id, so this has to be checked before saving.
    const isNew = section._id === '';
    setSavingIds(prev => new Set(prev).add(tid));
    setErrorIds(prev => { const n = new Set(prev); n.delete(tid); return n; });
    try {
      const result = await api.upsertObject('sb_section', section);
      // upsertObject hands the object back even when the db write failed, so check the response too.
      const written = isNew ? !!result?.body?.id : result?.body?.success === true;
      const saved = result?.res as SBSection | undefined;
      if (!written || !saved) throw new Error('Section was not saved');

      // Built from nodesRef rather than inside a setNodes updater, which React
      // doesn't always run right away (finalNodes would still be empty).
      const finalNodes = nodesRef.current.map(n =>
        n.section.true_id === tid ? { ...n, section: saved } : n
      );
      commitNodes(finalNodes);
      // New section: swap placeholder true_id for real _id everywhere
      if (isNew) {
        setExpandedIds(prev => {
          if (!prev.has(tid)) return prev;
          const n = new Set(prev); n.delete(tid); n.add(saved._id); return n;
        });
        setEditingIds(prev => {
          if (!prev.has(tid)) return prev;
          const n = new Set(prev); n.delete(tid); n.add(saved._id); return n;
        });
      }

      clearDirty(saved._id || tid);
      return { saved, finalNodes };
    } catch {
      setErrorIds(prev => new Set(prev).add(tid));
      return { saved: null, finalNodes: nodesRef.current };
    } finally {
      setSavingIds(prev => { const n = new Set(prev); n.delete(tid); return n; });
    }
  };

  // ---------------------------------------------------------------------------
  // Content mutations — mark dirty only, no save yet
  // ---------------------------------------------------------------------------

  const updateNode = (index: number, updated: SBSection) => {
    const next = [...nodesRef.current];
    next[index] = { ...next[index], section: updated };
    commitNodes(next);
  };

  // ---------------------------------------------------------------------------
  // Structural mutations
  // ---------------------------------------------------------------------------

  const addSection = async () => {
    const s = new SBSection();
    s.parent_id = source_book._id;
    s.parent_type = 'Source Book';
    s.position = nodesRef.current.filter(n => n.depth === 0).length;
    const { saved: s2, finalNodes } = await saveSectionContent(s);
    if (s2) {
      const next = [...finalNodes, { section: s2, depth: 0 }];
      commitNodes(next);
      expand(s2.true_id);
      setEditingIds(prev => new Set(prev).add(s2.true_id));
      saveStructure(next);
    }
  };

  /**
   * Sections can end up missing from the source book's saved structure.
   * Each section individually knows its parent, so this finds every section
   * for the source book in the db and adds any that are missing back in under
   * their recorded parent. Nothing already in the structure is moved or removed.
   */
  const missingSections = async () => {
    setCheckingMissing(true);
    setMissingMessage('');
    try {
      const all_sections = await api.getSourceBookSectionsLive(source_book._id);
      const current = nodesRef.current;
      const known_ids = new Set(current.map(n => n.section._id));
      const missing = all_sections.filter(o => !known_ids.has(o._id));
      if (missing.length === 0) {
        setMissingMessage('No missing sections found.');
        return;
      }

      const missing_ids = new Set(missing.map(o => o._id));
      const byPosition = (a: SBSection, b: SBSection) => a.position - b.position;
      const subtree = (section: SBSection, depth: number): FlatNode[] => [
        { section, depth },
        ...missing
          .filter(o => o.parent_id === section._id)
          .sort(byPosition)
          .flatMap(o => subtree(o, depth + 1)),
      ];

      // The topmost missing sections hang off the source book or a section already in the structure.
      let next = [...current];
      const tops = missing.filter(o => !missing_ids.has(o.parent_id)).sort(byPosition);
      for (const section of tops) {
        const parentIndex = section.parent_type === 'SBSection'
          ? next.findIndex(n => n.section._id === section.parent_id)
          : -1;
        if (parentIndex === -1) {
          next = [...next, ...subtree(section, 0)];
        } else {
          const parentDepth = next[parentIndex].depth;
          let insertAt = parentIndex + 1;
          while (insertAt < next.length && next[insertAt].depth > parentDepth) insertAt++;
          next = [...next.slice(0, insertAt), ...subtree(section, parentDepth + 1), ...next.slice(insertAt)];
        }
      }
      commitNodes(next);
      saveStructure(next);
      setMissingMessage(`Added ${missing.length} missing section${missing.length === 1 ? '' : 's'}.`);
    } finally {
      setCheckingMissing(false);
    }
  };

  // The index just past the last of a node's subsections (at any depth).
  const subtreeEnd = (index: number) => {
    const prev = nodesRef.current;
    const targetDepth = prev[index].depth;
    let end = index + 1;
    while (end < prev.length && prev[end].depth > targetDepth) end++;
    return end;
  };

  // Deletes sections from the db a few at a time, so a big section doesn't send hundreds of requests at once.
  const deleteSections = async (sections: SBSection[]) => {
    for (let i = 0; i < sections.length; i += 4) {
      await Promise.all(sections.slice(i, i + 4).map(s =>
        api.deleteObject('sb_section', s).catch(ex => console.error(ex))
      ));
    }
  };

  /**
   * Removes nodes[start, end) from the tree, saves the structure, and deletes their sections.
   */
  const removeNodes = (start: number, end: number) => {
    const prev = nodesRef.current;
    const removing = prev.slice(start, end);
    removing.forEach(n => {
      const tid = n.section.true_id;
      setExpandedIds(p => { const x = new Set(p); x.delete(tid); return x; });
      setEditingIds(p => { const x = new Set(p); x.delete(tid); return x; });
      clearDirty(tid);
    });

    const remaining = [...prev.slice(0, start), ...prev.slice(end)];
    commitNodes(remaining);
    saveStructure(remaining);
    deleteSections(removing.map(n => n.section).filter(s => s._id !== ''));
  };

  const deleteNode = (index: number) => {
    removeNodes(index, subtreeEnd(index));
  };

  const deleteAllSubsections = (index: number) => {
    const end = subtreeEnd(index);
    const count = end - index - 1;
    if (count === 0) return;
    const name = nodesRef.current[index].section.name || 'Untitled Section';
    if (!window.confirm(`Delete all ${count} subsection${count === 1 ? '' : 's'} of "${name}"? This can't be undone.`)) return;
    removeNodes(index + 1, end);
  };

  const addChildSection = async (parentIndex: number) => {
    const prev = nodesRef.current;
    const parentDepth = prev[parentIndex].depth;
    let insertAt = parentIndex + 1;
    while (insertAt < prev.length && prev[insertAt].depth > parentDepth) insertAt++;

    const siblingCount = prev
      .slice(parentIndex + 1, insertAt)
      .filter(n => n.depth === parentDepth + 1).length;

    const s = new SBSection();
    s.parent_id = prev[parentIndex].section.true_id;
    s.parent_type = 'SBSection';
    s.position = siblingCount;

    const { saved: s2, finalNodes } = await saveSectionContent(s);
    if (!s2) return;

    // Recompute insertAt against finalNodes in case ids shifted
    const parentTid = prev[parentIndex].section.true_id;
    const newParentIndex = finalNodes.findIndex(n => n.section.true_id === parentTid);
    const newParentDepth = finalNodes[newParentIndex]?.depth ?? parentDepth;
    let newInsertAt = newParentIndex + 1;
    while (newInsertAt < finalNodes.length && finalNodes[newInsertAt].depth > newParentDepth) newInsertAt++;

    const next = [
      ...finalNodes.slice(0, newInsertAt),
      { section: s2, depth: newParentDepth + 1 },
      ...finalNodes.slice(newInsertAt),
    ];

    commitNodes(next);
    expand(finalNodes[newParentIndex].section.true_id);
    expand(s2.true_id);
    setEditingIds(p => new Set(p).add(s2.true_id));
    saveStructure(next);
  };

  const indentNode = (index: number) => {
    const prev = nodesRef.current;
    if (index === 0) return;
    const currentDepth = prev[index].depth;
    let newParentIndex = index - 1;
    while (newParentIndex >= 0 && prev[newParentIndex].depth > currentDepth) newParentIndex--;
    if (newParentIndex < 0 || prev[newParentIndex].depth !== currentDepth) return;

    let end = index + 1;
    while (end < prev.length && prev[end].depth > currentDepth) end++;
    const moving = prev.slice(index, end).map(n => ({ ...n, depth: n.depth + 1 }));

    let insertAt = newParentIndex + 1;
    while (insertAt < index && prev[insertAt].depth > prev[newParentIndex].depth) insertAt++;
    const next = [...prev.slice(0, index), ...prev.slice(end)];
    next.splice(newParentIndex + 1 + (insertAt - (newParentIndex + 1)), 0, ...moving);
    commitNodes(next);
    saveStructure(next);
  };

  const outdentNode = (index: number) => {
    const prev = nodesRef.current;
    const currentDepth = prev[index].depth;
    if (currentDepth === 0) return;

    let end = index + 1;
    while (end < prev.length && prev[end].depth > currentDepth) end++;
    const moving = prev.slice(index, end).map(n => ({ ...n, depth: n.depth - 1 }));

    const next = [...prev.slice(0, index), ...prev.slice(end)];
    const removedCount = end - index;
    let insertAt = end - removedCount;
    while (insertAt < next.length && next[insertAt].depth > currentDepth - 1) insertAt++;
    next.splice(insertAt, 0, ...moving);
    commitNodes(next);
    saveStructure(next);
  };

  // ---------------------------------------------------------------------------
  // Drag and drop
  // ---------------------------------------------------------------------------

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    dragOverIndex.current = index;
  };

  const handleDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    const fromIndex = Number(e.dataTransfer.getData('text/plain'));
    if (fromIndex === toIndex) return;

    const prev = nodesRef.current;
    const fromDepth = prev[fromIndex].depth;
    let fromEnd = fromIndex + 1;
    while (fromEnd < prev.length && prev[fromEnd].depth > fromDepth) fromEnd++;
    const moving = prev.slice(fromIndex, fromEnd);

    const next = [...prev.slice(0, fromIndex), ...prev.slice(fromEnd)];
    const adjustedTo = toIndex > fromIndex ? toIndex - (fromEnd - fromIndex) : toIndex;
    next.splice(Math.max(0, adjustedTo), 0, ...moving);
    commitNodes(next);
    saveStructure(next);
    dragOverIndex.current = null;
  };

  // ---------------------------------------------------------------------------
  // Save / Cancel for content edits
  // ---------------------------------------------------------------------------

  const handleSave = async (index: number) => {
    const current = nodesRef.current;
    const node = current[index];
    const s = node.section.clone();
    const { parent_id, parent_type, parentKey } = computeParentInfo(current, index);
    s.parent_id = parent_type === 'Source Book' ? source_book._id : parent_id;
    s.parent_type = parent_type;
    let sibPos = 0;
    for (let i = 0; i < index; i++) {
      if (computeParentInfo(current, i).parentKey === parentKey) sibPos++;
    }
    s.position = sibPos;
    const { finalNodes } = await saveSectionContent(s);
    // After content save, also persist structure so the source book reflects
    // the latest section data (name, special_type, etc.) in its sections array.
    saveStructure(finalNodes);
  };

  const handleCancel = (index: number) => {
    const tid = nodesRef.current[index].section.true_id;
    const original = dirtyMap.get(tid);
    if (original) {
      updateNode(index, original.clone());
      clearDirty(tid);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      {nodes.length === 0 && (
        <span style={{ color: '#888' }}>No sections.</span>
      )}
      {nodes.map((node, i) => {
        const { section, depth } = node;
        const tid = section.true_id;
        const expanded = expandedIds.has(tid);
        const editing = editingIds.has(tid);
        const loadingEdit = loadingIds.has(tid);
        const dirty = isDirty(tid);
        const saving = savingIds.has(tid);
        const hasError = errorIds.has(tid);
        const structuralDisabled = dirty || saving;

        // Hide if any ancestor is collapsed
        if (depth > 0) {
          let j = i - 1;
          while (j >= 0 && nodes[j].depth >= depth) j--;
          if (j >= 0 && !expandedIds.has(nodes[j].section.true_id)) return null;
          let ancestor = j;
          while (ancestor > 0) {
            let k = ancestor - 1;
            while (k >= 0 && nodes[k].depth >= nodes[ancestor].depth) k--;
            if (k < 0) break;
            if (!expandedIds.has(nodes[k].section.true_id)) return null;
            ancestor = k;
          }
        }

        const canIndent = !structuralDisabled && i > 0 && (() => {
          let j = i - 1;
          while (j >= 0 && nodes[j].depth > depth) j--;
          return j >= 0 && nodes[j].depth === depth;
        })();
        const canOutdent = !structuralDisabled && depth > 0;

        let childCount = 0;
        for (let j = i + 1; j < nodes.length && nodes[j].depth > depth; j++) {
          if (nodes[j].depth === depth + 1) childCount++;
        }

        const renderSpecialSectionOptions = () => {
          switch (section.special_type) {
            case "Classes":
              return (
                <Grid size={{ xs: 6 }}>
                  <CheckBox
                    name="Include Spell List"
                    value={section.special_attributes.include_spell_list ?? false}
                    onChange={(changed: boolean) => {
                      const s = section.clone();
                      s.special_attributes.include_spell_list = changed;
                      markDirty(tid, section);
                      updateNode(i, s);
                    }}
                  />
                </Grid>
              );
            case "Items": 
              if (true) {
                const special_attributes = section.special_attributes as { item_types?: ItemType[], magic?: boolean };
                const item_types = special_attributes.item_types ?? [];
                const magic = special_attributes.magic;
                return (
                  <>
                    <Grid size={{ xs: 6 }}>
                      <SelectStringBox
                        name="Item Types"
                        options={ITEM_TYPES}
                        values={item_types}
                        multiple
                        onChange={(changed: ItemType[]) => {
                          const s = section.clone();
                          s.special_attributes.item_types = changed;
                          markDirty(tid, section);
                          updateNode(i, s);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <SelectStringBox
                        name="Magic"
                        options={["Yes", "No", "Ignore"]}
                        value={magic === undefined ? "Ignore" : (magic ? "Yes" : "No")}
                        onChange={(changed: "Yes" | "No" | "Ignore") => {
                          const s = section.clone();
                          if (changed === "Ignore") {
                            delete s.special_attributes.magic;
                          } else {
                            s.special_attributes.magic = changed === "Yes";
                          }
                          markDirty(tid, section);
                          updateNode(i, s);
                        }}
                      />
                    </Grid>
                  </>
                );
              }
            break;
            // case "Feats": 
            //   if (true) {
            //     const special_attributes = section.special_attributes as { feat_tiers?: FeatTier[] };
            //     const feat_tiers = special_attributes.feat_tiers ?? [];
            //     return (
            //       <>
            //         <Grid size={{ xs: 12 }}>
            //           <SelectStringBox
            //             name="Feat Types"
            //             options={FEAT_TIERS}
            //             values={feat_tiers}
            //             multiple
            //             onChange={(changed: FeatTier[]) => {
            //               const s = section.clone();
            //               s.special_attributes.feat_tiers = changed;
            //               markDirty(tid, section);
            //               updateNode(i, s);
            //             }}
            //           />
            //         </Grid>
            //       </>
            //     );
            //   }
            // break;
          }
          return null;
        }

        return (
          <div
            key={tid}
            draggable={!structuralDisabled}
            style={{ marginLeft: depth * 24 }}
            onDragStart={e => handleDragStart(e, i)}
            onDragOver={e => handleDragOver(e, i)}
            onDrop={e => handleDrop(e, i)}
          >
            <Paper variant="outlined" style={{ marginBottom: 8, overflow: 'hidden' }}>
              {/* Header */}
              <Grid container spacing={1} alignItems="center" direction="row" style={{ padding: '4px 8px' }}>
                <Grid style={{ display: 'flex', alignItems: 'center' }}>
                  <DragIndicatorIcon
                    fontSize="small"
                    color={structuralDisabled ? 'disabled' : 'action'}
                    style={{ cursor: structuralDisabled ? 'not-allowed' : 'grab' }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }} style={{ cursor: 'pointer' }} onClick={() => toggleExpand(tid)}>
                  <span className="header">
                    {section.name || <em style={{ color: '#888' }}>Untitled Section</em>}
                  </span>
                </Grid>

                <Grid onClick={e => e.stopPropagation()} style={{ display: 'flex' }}>
                  <IconButton size="small" title="Outdent" disabled={!canOutdent} onClick={() => outdentNode(i)}>
                    <FormatIndentDecreaseIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Indent (make child of section above)" disabled={!canIndent} onClick={() => indentNode(i)}>
                    <FormatIndentIncreaseIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Delete" disabled={saving} onClick={() => deleteNode(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
                
                <Grid style={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    title={editing ? 'Hide edit controls' : 'Show edit controls'}
                    disabled={loadingEdit}
                    onClick={() => toggleEditing(tid, i)}
                  >
                    {loadingEdit
                      ? <CircularProgress size={16} />
                      : editing ? <EditOffIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                  </IconButton>
                  <span
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => toggleExpand(tid)}
                  >
                    <span style={{ fontSize: '0.75rem', color: '#888', marginRight: 2 }}>
                      {childCount} Subsection{childCount === 1 ? '' : 's'}
                    </span>
                    <IconButton size="small" title={expanded ? 'Collapse subsections' : 'Expand subsections'}>
                      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Grid>
              </Grid>

              {/* Edit controls */}
              <Collapse in={editing && !loadingEdit}>
                <Divider />
                <div style={{ padding: '8px 12px' }}>
                  <Grid container style={{ marginBottom: 8 }}>
                    <Grid size={{ xs: 4 }}>
                      <StringBox
                        name="Section Name"
                        value={section.name}
                        onBlur={(val: string) => {
                          const s = section.clone();
                          s.name = val;
                          markDirty(tid, section);
                          updateNode(i, s);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <SelectStringBox
                        name="Special Section Type"
                        options={SPECIAL_SECTION_TYPES}
                        value={section.special_type}
                        onChange={(val: string) => {
                          const s = section.clone();
                          if (isSpecialSectionType(val)) {
                            s.special_type = val;
                          }
                          markDirty(tid, section);
                          updateNode(i, s);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <CheckBox
                        name="Single Page"
                        value={section.single_page}
                        onChange={(val) => {
                          const s = section.clone();
                          s.single_page = val;
                          markDirty(tid, section);
                          updateNode(i, s);
                        }}
                      />
                    </Grid>
                    {renderSpecialSectionOptions()}
                  </Grid>
                  <Grid container style={{ marginBottom: 8 }}>
                    <Grid size={{ xs: 12 }}>
                      <DescriptionBox
                        name="Content"
                        value={section.content}
                        onChange={(val: string) => {
                          const s = section.clone();
                          s.content = val;
                          markDirty(tid, section);
                          updateNode(i, s);
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Save status + buttons */}
                  <Grid container alignItems="center" spacing={1} style={{ marginBottom: 8 }}>
                    <Grid style={{ display: 'flex', alignItems: 'center', minWidth: 24 }}>
                      {saving ? (
                        <CircularProgress size={16} />
                      ) : hasError ? (
                        <ErrorIcon fontSize="small" color="error" titleAccess="Save failed" />
                      ) : !dirty ? (
                        <CheckCircleIcon fontSize="small" color="success" titleAccess="Saved" />
                      ) : null}
                    </Grid>
                    <Grid>
                      <span style={{ fontSize: '0.75rem', color: saving ? '#888' : hasError ? 'red' : dirty ? '#888' : 'green' }}>
                        {saving ? 'Saving…' : hasError ? 'Save failed' : dirty ? 'Unsaved changes' : 'Saved'}
                      </span>
                    </Grid>
                    {dirty && !saving && (
                      <>
                        <Grid>
                          <Button size="small" variant="contained" color="primary" onClick={() => handleSave(i)}>
                            Save
                          </Button>
                        </Grid>
                        <Grid>
                          <Button size="small" variant="outlined" onClick={() => handleCancel(i)}>
                            Cancel
                          </Button>
                        </Grid>
                      </>
                    )}
                    {hasError && !saving && (
                      <Grid>
                        <Button size="small" variant="contained" color="error" onClick={() => handleSave(i)}>
                          Retry
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </div>
              </Collapse>

              {/* Add Subsection */}
              <Collapse in={expanded}>
                <Divider />
                <div style={{ padding: '8px 12px' }}>
                  <Button size="small" startIcon={<AddIcon />} disabled={saving} onClick={() => addChildSection(i)}>
                    Add Subsection
                  </Button>
                  <Button size="small" color="error" startIcon={<DeleteIcon />} disabled={saving || childCount === 0}
                    style={{ marginLeft: 8 }} onClick={() => deleteAllSubsections(i)}>
                    Delete All Subsections
                  </Button>
                </div>
              </Collapse>
            </Paper>
          </div>
        );
      })}

      <p>
        <Button size="small" startIcon={<AddIcon />} onClick={addSection} style={{ marginTop: 4 }}>
          Add Section
        </Button>
      </p>
      <p>
        <Button size="small" startIcon={<AddIcon />} disabled={checkingMissing} onClick={missingSections} style={{ marginTop: 4 }}>
          Check for Missing Sections
        </Button>
        {checkingMissing && <CircularProgress size={16} style={{ marginLeft: 8 }} />}
        {missingMessage && <span style={{ fontSize: '0.75rem', color: '#888', marginLeft: 8 }}>{missingMessage}</span>}
      </p>
    </div>
  );
}